import { ImposeInterestFrequency, VoucherTypeSetting, } from '../../vouchers/index.js';
import { parseDate, toISODate } from '../utils.js';
import { OPEN_ENDED } from './common.js';
export const ceilToMonthStart = (time) => {
    const date = new Date(time);
    if (date.getUTCDate() === 1 &&
        date.getUTCHours() === 0 &&
        date.getUTCMinutes() === 0 &&
        date.getUTCSeconds() === 0 &&
        date.getUTCMilliseconds() === 0) {
        return time;
    }
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1);
};
const isStartedMonthVoucher = (caseVoucher) => caseVoucher.setting === VoucherTypeSetting.InterestPercentage &&
    caseVoucher.settingDetails.imposeInterestFrequency ===
        ImposeInterestFrequency.Monthly;
// Never memoises: calculate() mutates its inputs, so a spread-copied voucher
// can otherwise carry a stale parse.
export const addParsedInterestPeriods = (caseVoucher, day = (date) => parseDate(toISODate(date))) => {
    const periods = caseVoucher.interestPeriods;
    if (periods == null || periods.length === 0) {
        caseVoucher.parsedInterestPeriods = [];
        return caseVoucher;
    }
    const snapToMonths = isStartedMonthVoucher(caseVoucher);
    const interestStartDate = caseVoucher.parsedInterestStartDate ?? -Infinity;
    const parsed = [];
    for (const period of periods) {
        const rawFrom = day(period.from);
        const voidedAt = period.voidedAt !== null ? day(period.voidedAt) : null;
        let rateTo = period.to !== null ? day(period.to) : OPEN_ENDED;
        if (voidedAt !== null && voidedAt < rateTo) {
            rateTo = voidedAt;
        }
        let rateFrom = rawFrom;
        if (snapToMonths) {
            if (rawFrom > interestStartDate) {
                rateFrom = ceilToMonthStart(rawFrom);
            }
            if (rateTo !== OPEN_ENDED) {
                rateTo = ceilToMonthStart(rateTo);
            }
        }
        if (rateFrom >= rateTo) {
            continue;
        }
        parsed.push({
            id: period.id,
            rate: period.rate,
            rateFrom,
            rateTo,
            voidedAt,
        });
    }
    parsed.sort((a, b) => a.rateFrom - b.rateFrom);
    // Overlap is rejected on write; if it slips through, log (don't throw) and
    // let the earliest-starting period win.
    for (let i = 0; i < parsed.length - 1; i++) {
        if (parsed[i + 1].rateFrom < parsed[i].rateTo) {
            console.error(`[Interest periods]: Overlapping interest periods (${parsed[i].id}, ${parsed[i + 1].id}) on case voucher ${caseVoucher.caseVoucherId}. The earliest starting period takes precedence in the overlap.`);
        }
    }
    caseVoucher.parsedInterestPeriods = parsed;
    return caseVoucher;
};
export const initShadowInterestStates = (caseVoucher) => {
    const periods = caseVoucher.parsedInterestPeriods;
    if (periods == null || periods.length === 0) {
        return undefined;
    }
    let states;
    for (const period of periods) {
        if (period.voidedAt === null) {
            continue;
        }
        if (states === undefined) {
            states = {};
        }
        states[period.id] = {
            voidedAt: period.voidedAt,
            hypExtraAmount: 0,
            hypExtraCapitalized: 0,
            applied: false,
            hist: [],
        };
    }
    return states;
};
const baseRateAt = (baseRates, time) => {
    let rate = baseRates.length > 0 ? baseRates[0].rate : 0;
    for (const baseRate of baseRates) {
        if (baseRate.time == null || baseRate.time <= time) {
            rate = baseRate.rate;
        }
        else {
            break;
        }
    }
    return rate;
};
// baseRates must be ordered oldest to newest.
export const overlayInterestPeriods = (baseRates, periods, from, to) => {
    if (periods.length === 0) {
        return baseRates;
    }
    const boundaries = new Set([from]);
    for (const baseRate of baseRates) {
        if (baseRate.time != null && baseRate.time > from && baseRate.time < to) {
            boundaries.add(baseRate.time);
        }
    }
    for (const period of periods) {
        if (period.rateFrom < to && period.rateTo > from) {
            if (period.rateFrom > from) {
                boundaries.add(period.rateFrom);
            }
            if (period.rateTo < to) {
                boundaries.add(period.rateTo);
            }
        }
    }
    const orderedBoundaries = [...boundaries].sort((a, b) => a - b);
    const result = [];
    for (const boundary of orderedBoundaries) {
        const base = baseRateAt(baseRates, boundary);
        const covering = periods.find((period) => period.rateFrom <= boundary && boundary < period.rateTo);
        const entry = covering
            ? {
                time: boundary,
                rate: covering.rate,
                baseRate: base,
                voidedPeriodId: covering.voidedAt !== null ? covering.id : undefined,
            }
            : { time: boundary, rate: base, baseRate: base };
        const previous = result[result.length - 1];
        if (previous &&
            previous.rate === entry.rate &&
            previous.baseRate === entry.baseRate &&
            previous.voidedPeriodId === entry.voidedPeriodId) {
            continue;
        }
        result.push(entry);
    }
    return result;
};
// Lump at voidedAt so only post-default payments distribute against it.
export const applyShadowLump = (caseVoucher, shadow, appendHist) => {
    shadow.applied = true;
    if (shadow.hypExtraAmount !== 0) {
        caseVoucher.amount += shadow.hypExtraAmount;
        caseVoucher.amountUpdated = true;
        if (caseVoucher.setting === VoucherTypeSetting.CompoundInterest) {
            caseVoucher.amountAtCapitalization =
                (caseVoucher.amountAtCapitalization || 0) + shadow.hypExtraCapitalized;
        }
    }
    // Append reapplied rows even when the lump nets to 0: the charged amount
    // was added without its own row, so the reapplied row carries it.
    if (appendHist && shadow.hist.length > 0) {
        const entries = shadow.hist.map((entry) => ({
            ...entry,
            reapplied: true,
        }));
        caseVoucher.interestHistory = (caseVoucher.interestHistory || []).concat(entries);
    }
};
export const appendShadowHist = (state, entry) => {
    const last = state.hist[state.hist.length - 1];
    if (last &&
        last.to === entry.from &&
        last.interest === entry.interest &&
        last.referenceVoucherBalance === entry.referenceVoucherBalance &&
        last.capitalizedInterestBalance === entry.capitalizedInterestBalance) {
        last.to = entry.to;
        last.amount += entry.amount;
        return;
    }
    state.hist.push(entry);
};
//# sourceMappingURL=periods.js.map